<template>
    <div class="container mx-auto">
        <!--Introduction-->
        <section class="bg-slate-50/60 my-4">
            <div class="mx-auto mb-20 lg:mb-0 lg:p-20">
                <div class="grid items-start gap-12 lg:grid-cols-2">
                    <!-- Left copy -->
                    <div>
                        <h6 class="text-blue-700 font-semibold tracking-wide uppercase text-xs">Euro-Titans</h6>
                        <h1 class="font-extrabold">Chartanalyse der besten europäischen Aktien</h1>

                        <ul class="mt-8 space-y-4">
                            <FeatureItem title="Täglich aktuell" desc="Eine Chartanalyse von vier Europa-Aktien jeden Handelstag" />
                            <FeatureItem title="Klare Handelsmöglichkeiten" desc="Einstiegs- und Ausstiegszonen auf Basis der Elliott-Wellen-Analyse" />
                            <FeatureItem title="Musterportfolio" desc="Unser Musterportfolio macht unsere Performance und Handelsentscheidungen jederzeit nachvollziehbar" />
                            <FeatureItem title="Top-Aktien aus ganz Europa" desc="Von Novo Nordisk, Nestlé, Ferrari über SAP und Volkswagen bis zu ASML" />
                        </ul>
                    </div>

                    <!-- Right pricing card -->
                    <div id="pricing-card">
                        <div class="overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-slate-200">
                            <!-- Header -->
                            <div class="flex flex-col items-center gap-3 border-b border-slate-100 px-8 py-8 text-center">
                                <img class="w-20 h-auto object-cover bg-slate-100 rounded-full" :src="productGroup.image" />
                                <div class="space-y-1">
                                    <h3 class="text-lg font-semibold">Preisübersicht für das Euro-Titans-Aktienpaket</h3>
                                    <p class="text-sm text-slate-500">Wählen Sie Ihre Laufzeit!</p>
                                </div>
                            </div>

                            <!-- Price options -->
                            <ul v-for="productVariant in sortedProductVariants" :key="productVariant.id" class="divide-y divide-slate-100">
                                <PriceRow
                                    :label="$translate(getAttributeByType(productVariant, 'period').title)"
                                    :badge="productVariant.discount_percent * 100 === 10 ? 'Beliebt' : ''"
                                    :note="$t('per_month')"
                                    :sub="productVariant.discount_percent > 0 ? '-' + $toPercent(productVariant.discount_percent, 0) : ''"
                                    :price="$toCurrency(productVariant.base_price_gross - productVariant.base_price_gross * productVariant.discount_percent)"
                                    :class="selectedProductVariant === productVariant ? 'bg-blue-dark text-white' : ''"
                                    @click="$emit('select-product', productVariant)"
                                />
                            </ul>

                            <!-- CTA -->
                            <div class="px-6 pb-6 pt-4">
                                <button
                                    type="button"
                                    class="group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue px-5 py-3 text-sm font-semibold text-white shadow-md hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                                    @click="$emit('add-to-cart')"
                                >
                                    <span>Euro-Titans-Aktienpaket jetzt abonnieren</span>
                                    <i class="fi fi-rs-arrow-right flex align-middle text-white" />
                                </button>

                                <p class="mt-3 text-center text-xs text-slate-500">Sofort nach Buchung in Ihrem HKCM‑Dashboard und in unserer App abrufbar.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner">
            <div class="max-w-6xl mx-auto">
                <!-- Header Block -->
                <div class="text-center mb-16">
                    <h2 class="text-2xl lg:text-3xl font-extrabold text-slate-900 leading-snug mb-6">So funktioniert unser Euro-Titans-Paket</h2>
                    <p class="text-slate-600 max-w-4xl mx-auto text-sm lg:text-base">
                        Das Euro-Titans-Aktienpaket der HKCM bietet Ihnen werktäglich (Montag bis Freitag) detaillierte Analysen zu vier der bedeutendsten europäischen Aktien,
                        darunter Allianz, ASML, LVMH oder Siemens. So bekommen Sie pro Woche die Analyse von 20 Europa Aktien und alle zwei Wochen alle 40 Werte. Jede Analyse zeigt
                        Ihnen mithilfe der Elliott-Wellen-Analyse konkrete Zielzonen, sodass Sie leicht erkennen können, wann sich Ein- oder Ausstiege sowie Anpassungen Ihrer
                        Position anbieten. Außerdem führen wir ein Musterportfolio, in dem alle vorgenommenen Anpassungen dokumentiert werden, damit Sie Ihre Entscheidungen stets
                        auf einer klaren Grundlage treffen können.
                    </p>
                </div>

                <!-- Features Block -->
                <div>
                    <h3 class="mt-12 mb-10 text-xl font-bold text-center text-slate-800">Das erhalten Sie konkret:</h3>

                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                        <!-- Card 1 -->
                        <FeatureCard
                            title="Täglich eine schriftliche Analyse"
                            text="von vier der besten europäischen Aktien. Direkt per E-Mail, in Ihrem HKCM-Dashboard und in der App."
                            :icon-class="'fi fi-rr-envelope-open-text'"
                        />

                        <!-- Card 2 -->
                        <FeatureCard
                            title="Klare Handelsmöglichkeiten"
                            text="mit klaren Einstiegs- und Ausstiegszonen für fundierte Entscheidungen: Per Mail, direkt in die App (inklusive Push-Benachrichtigung) oder jederzeit im HKCM-Dashboard."
                            :icon-class="'fi fi-rr-chart-line-up'"
                        />

                        <!-- Card 6 -->
                        <FeatureCard
                            title="Für alle Trader und Investoren"
                            text="Ob langfristig oder kurzfristig, ob Sie in Aktien, CFDs oder Derivate investieren – Sie profitieren stets von klar definierten Einstiegs-, Ausstiegs-, Take-Profit- und Stoppmarken, die Ihnen Orientierung und Struktur im Handel bieten. "
                            :icon-class="'fi fi-rr-eye'"
                        />

                        <!-- Card 4 -->
                        <FeatureCard
                            title="Übersichtliche Chartbilder"
                            text="Mit klar markierten Unterstützungen, Widerständen und Zielzonen geben Ihnen die Chartbilder eine wertvolle Orientierung, sowohl auf der Long- als auch auf der Short-Seite. Sie machen die aktuellen Marktstrukturen leicht verständlich und ermöglichen es Ihnen, den Überblick über entscheidende Bewegungen zu behalten. "
                            :icon-class="'fi fi-rr-bolt'"
                        />

                        <!-- Card 5 -->
                        <FeatureCard
                            title="Schnelle Reaktionen auf Marktbewegungen"
                            text="Bei starken Kursbewegungen oder wichtigen Ereignissen erhalten Sie kurze Updates, direkt als Kurznachricht in Ihrem Dashboard oder in der HKCM-App. So bleiben Sie jederzeit über alle relevanten Marktveränderungen informiert und können schnell reagieren. "
                            :icon-class="'fi fi-rr-medal'"
                        />

                        <!-- Card 3 -->
                        <FeatureCard
                            title="Alle 40 Aktien im zweiwöchigen Turnus"
                            text="Innerhalb von zwei Wochen werden alle 40 europäischen Spitzen-Aktien für Sie analysiert."
                            :icon-class="'fi fi-rr-bullseye'"
                        />
                    </div>
                </div>
            </div>
        </section>

        <section class="w-full mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20">
            <h2 class="text-center mb-4">
                <span class="text-sm text-gray-600">Europäische Aktien-Liste</span>
                <br />
                Diese Aktien sind im Euro-Titans-Paket enthalten:
            </h2>
            <div class="px-4">
                <div class="mb-4 w-full">
                    <!-- Aktien pro Kategorie -->
                    <div class="grid lg:grid-cols-2 gap-8 justify-items-start px-4">
                        <div v-for="gruppe in gruppierteAktien" :key="gruppe.category" class="mb-4 w-full">
                            <!-- Kategoriename -->
                            <h3 class="text-xl mb-2 text-center">
                                {{ gruppe.category }}
                            </h3>

                            <!-- Aktien pro Kategorie -->
                            <div class="grid grid-cols-2 lg:grid-cols-3 gap-2">
                                <div
                                    v-for="(aktie, idx) in gruppe.aktien"
                                    :key="idx"
                                    class="bg-blue text-white flex items-center px-3 py-1 rounded-lg w-full text-center justify-center lg:min-h-12 cursor-pointer"
                                    @click="marketingModalOpen = aktie"
                                >
                                    <span>{{ aktie.title }}</span>

                                    <teleport to="body">
                                        <Popup type="text" :overflow-hidden="true" :open="marketingModalOpen === aktie" @open="closeAction()">
                                            <p class="pt-4">{{ aktie.description }}</p>
                                        </Popup>
                                    </teleport>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section
            v-if="productGroup.meta?.shopifyProductIds?.length > 0"
            class="lg:bg-gray-200 p-5 sm:p-10 lg:p-20 mb-20 lg:mb-0 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner"
        >
            <h2 class="text-center mb-4">Unsere Tassen</h2>
            <div class="gap-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 place-self-center justify-items-stretch">
                <div v-for="shopifyProductId in productGroup.meta?.shopifyProductIds" :key="shopifyProductId" class="w-full h-full flex">
                    <ShopifyProduct :product-id="shopifyProductId" bg-color="bg-white" class="h-full" />
                </div>
            </div>
        </section>

        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20">
            <strong data-aos="fade-right">
                <h3 class="px-4 pt-10 text-center">Europa Titans – FAQ</h3>
            </strong>
            <div>
                <div v-for="(faq, key) in faqs" :key="key" data-aos="fade-up">
                    <Accordeon :title="faq.question" :content="faq.answer" />
                </div>
            </div>
        </section>

        <section class="py-12 md:py-16 lg:py-20 mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner">
            <div class="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <!-- Textbereich -->
                <div>
                    <h2 class="mb-4">Europäische Aktien: Das bietet unsere Analyse</h2>
                    <p class="text-gray-600 mb-8">
                        Anhand der Elliott-Wellen-Methodik analysieren wir 40 europäische Aktien von Top-Unternehmen, aus den Bereichen Tech, Pharma, Luxus, Verteidigung &
                        Luft-/Raumfahrt, Energiewende & Industrie, Automobil, Banking, Fintech, Konsumgüter, Industrie, Telekommunikation, Öl und Gas. Anhand der
                        Elliott-Wellen-Methodik entwickeln wir konkrete Handelsszenarien für unterschiedliche Ansätze.
                        <br /><br />
                        Die Analyse konzentriert sich dabei auf:
                    </p>

                    <div class="flex flex-col gap-6">
                        <FeatureItem title="Strukturiert aufgebaute Chartbilder" desc="mit klar markierten Einstiegs-, Ausstiegs-, Unterstützungs- und Widerstandsbereichen. " />
                        <FeatureItem title="Konkrete Zielzonen" desc="zur Orientierung bei Kauf-, Verkaufs- oder Positionsanpassungen. " />
                        <FeatureItem title="Transparenz durch Musterportfolio" desc="alle Einstiege, Ausstiege, Nachkäufe und Stopps werden offen nachvollziehbar dokumentiert. " />
                    </div>
                </div>

                <!-- Bildbereich -->
                <div class="relative">
                    <div class="overflow-hidden rounded-2xl shadow-lg bg-gradient-to-b from-gray-800 to-gray-700 flex items-center justify-center h-80">
                        <img :src="$asset('/hkcm/images/unsereAnalyse.png')" alt="Chart Illustration" class="object-cover lg:object-fill w-full h-full bg-white" />
                    </div>
                </div>
            </div>
        </section>

        <section>
            <div class="mx-auto px-0 sm:px-6 lg:px-8 py-14 prose prose-slate max-w-none">
                <div class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 flex flex-col-reverse lg:flex-row items-center gap-8">
                    <div class="lg:w-1/2 m-auto justify-center">
                        <h2>Was versteht man unter europäischen Aktien?</h2>
                        <p class="text-slate-600">
                            In unserem Euro-Titans-Paket finden Sie europäische Aktien. Das sind Anteilsscheine von Unternehmen, deren Hauptsitz in Europa liegt – und das umfasst
                            mehr als nur die Staaten der Europäischen Union.

                            <br /><br />

                            Neben Konzernen aus Deutschland, Frankreich, den Niederlanden oder Italien gehören auch Unternehmen aus Ländern wie der Schweiz, Norwegen oder dem
                            Vereinigten Königreich dazu. Entscheidend ist also nicht die EU-Mitgliedschaft, sondern der geografische Standort des Unternehmens.

                            <br /><br />

                            Unter den bekanntesten europäischen Aktien finden sich Namen wie Nestlé und Roche aus der Schweiz, ASML aus den Niederlanden, LVMH aus Frankreich oder
                            Siemens und SAP aus Deutschland. Gemeinsam repräsentieren sie die wirtschaftliche Stärke Europas in Bereichen wie Technologie, Luxusgüter, Industrie,
                            Pharma, Automobil und Energie.
                        </p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/eurotitans_one.jpg')" alt="Tech Aktien" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>

                <div
                    class="bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 flex flex-col-reverse lg:flex-row-reverse items-center gap-8"
                >
                    <div class="lg:w-1/2 m-auto flex-col justify-center">
                        <h2>Welche Aktien gibt es in Europa?</h2>
                        <p class="text-slate-600">
                            In Europa gibt es eine große Vielfalt an Aktien aus unterschiedlichsten Branchen, die das wirtschaftliche Rückgrat des Kontinents bilden. Zu den
                            führenden Industrie- und Technologiewerten zählen Siemens, ASML, Airbus und SAP, während Konzerne wie BASF, Bayer und Merck die starke Stellung Europas
                            in Chemie und Pharma unterstreichen.
                            <br /><br />

                            Im Luxus- und Konsumsektor prägen Marken wie LVMH, Hermès, Christian Dior, Ferrari und L’Oréal das Bild, während Nestlé und Pernod Ricard für
                            internationale Konsumgüter stehen. Auch im Finanz- und Versicherungsbereich ist Europa breit aufgestellt, etwa mit Allianz, Deutsche Bank oder Adyen.

                            <br /><br />

                            Hinzu kommen Unternehmen aus den Bereichen Energie und Verteidigung wie TotalEnergies, Rheinmetall und Rolls-Royce Holdings. Auch die Autoindustrie
                            spielt eine zentrale Rolle in Europa, vertreten durch Marken wie BMW, Mercedes-Benz, Porsche und Volkswagen, die weltweit für Qualität und technische
                            Innovation stehen.
                        </p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/eurotitans_two.jpg')" alt="Tech Aktien" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>

                <div class="mb-20 lg:mb-0 lg:p-20 flex flex-col-reverse lg:flex-row items-center gap-8">
                    <div class="lg:w-1/2 m-auto flex-col justify-center">
                        <h2>Sind europäische Aktien eine gute Investition?</h2>
                        <p class="text-slate-600">
                            Europäische Aktien gelten als solide und langfristig attraktive Investition, weil viele der hier ansässigen Unternehmen seit Jahrzehnten zu den globalen
                            Marktführern zählen. Europas Wirtschaft hat sich aus traditionsreichen Industrien entwickelt. Von deutscher Ingenieurskunst über französische
                            Luxusmarken bis hin zu skandinavischer Innovationskraft.
                            <br /><br />
                            Firmen wie Siemens, SAP, ASML, LVMH oder Novo Nordisk stehen exemplarisch für diese Entwicklung: Aus regionalen Konzernen wurden weltweit führende
                            Technologietreiber und Markenikonen. Europas Stärke liegt in seiner Branchenvielfalt: von Industrie, Chemie und Automobil bis zu Luxus, Pharma, Rüstung
                            und Energie.
                            <br /><br />
                            Diese breite Aufstellung, kombiniert mit hoher Innovationskraft und stabilen Unternehmensstrukturen, macht europäische Aktien zu einer wertbeständigen
                            und gleichzeitig zukunftsorientierten Anlageoption.
                        </p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/eurotitans_three.jpg')" alt="Tech Aktien" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>
            </div>
        </section>

        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner text-left">
            <h2 class="text-center">Was sind die besten europäischen Aktien?</h2>
            <p class="text-slate-600 m-auto">
                Welche als die besten europäischen Aktien gelten, hängt immer von der individuellen Anlagestrategie und den Marktbedingungen ab. Eine allgemeingültige Antwort gibt
                es daher nicht. Klar ist jedoch: Europa beheimatet zahlreiche Unternehmen, die weltweit zu den führenden ihrer Branchen zählen.
                <br /><br />
                In unserem Euro-Titans-Paket sind viele dieser Spitzenwerte vereint, von Technologieführern wie ASML und SAP über Luxusmarken wie LVMH, Hermès und Ferrari bis hin
                zu starken Industriekonzernen wie Siemens, Airbus und Rheinmetall. Ergänzt wird das Paket durch bedeutende Unternehmen aus den Bereichen Pharma, Konsumgüter,
                Finanzen und Energie.
                <br /><br />
                Damit bietet das Euro-Titans-Paket eine ausgewogene Auswahl der erfolgreichsten und innovativsten Aktien Europas und damit einen umfassenden Überblick über die
                wirtschaftliche Elite des Kontinents.
            </p>
        </section>

        <section class="mb-20 lg:mb-0 bg-slate-50">
            <div class="max-w-6xl mx-auto px-4 py-12 lg:py-20">
                <!-- Textbereich -->
                <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center mb-6">Rüstungs-Aktien Europa</h2>
                <p class="text-slate-600 leading-relaxed text-sm lg:text-base text-center lg:text-left">
                    Europäische Rüstungsaktien gewinnen zunehmend an Bedeutung, da viele Länder ihre Verteidigungsbudgets angesichts geopolitischer Entwicklungen deutlich erhöhen.
                    Zu den führenden Unternehmen der Branche zählen Rheinmetall aus Deutschland, Airbus mit seinem Geschäftsbereich Defence and Space, Rolls-Royce Holdings aus dem
                    Vereinigten Königreich sowie MTU Aero Engines als bedeutender Hersteller von Triebwerkskomponenten.
                    <br /><br />
                    Diese Unternehmen entwickeln und produzieren modernste Technologien für Militär, Luftfahrt und Sicherheitssysteme und sind damit zentrale Akteure in Europas
                    Verteidigungsindustrie. Sie profitieren von einer wachsenden Nachfrage nach moderner Ausrüstung und nachhaltigen Sicherheitslösungen.
                    <br /><br />
                    Die europäische Rüstungsindustrie verbindet jahrzehntelange Ingenieurstradition mit technologischer Innovation und spielt eine Schlüsselrolle bei der Sicherung
                    strategischer Unabhängigkeit Europas.
                </p>
            </div>
        </section>

        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner text-left">
            <div class="max-w-6xl mx-auto px-4 py-12 lg:py-20">
                <!-- Textbereich -->
                <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center mb-6">Europäische Dividenden-Aktien</h2>
                <p class="text-sm text-slate-600 mb-4">
                    Dividendenaktien sind Unternehmensanteile, die ihren Aktionären regelmäßig einen Teil des Unternehmensgewinns in Form einer Dividende ausschütten.
                </p>
                <p class="text-slate-600 leading-relaxed text-sm lg:text-base text-center lg:text-left">
                    In Europa finden sich viele solide Dividendenzahler mit langer Ausschüttungstradition. Dazu gehören Allianz, Nestlé, TotalEnergies, Siemens, BASF und LVMH, die
                    für ihre verlässlichen und stetig steigenden Dividenden bekannt sind.
                    <br /><br />
                    Diese Unternehmen stehen stellvertretend für Europas Stärke in stabilen, ertragsorientierten Geschäftsmodellen. Von Industrie und Energie über Konsumgüter bis
                    hin zu Finanzen. Für Anleger, die Wert auf regelmäßige Erträge und langfristige Stabilität legen, gelten europäische Dividendenaktien als besonders attraktive
                    Anlageform.
                    <br /><br />
                    <span class="font-bold">Klassische Dividendenaktien aus unserem Euro-Titans-Paket:</span>
                    <br />
                    Allianz | BASF | Bayer | BMW | Mercedes-Benz Group | Nestlé | Novo Nordisk | LVMH | Hermès | TotalEnergies | Siemens | Volkswagen | Roche | L’Oréal | Pernod
                    Ricard
                </p>
            </div>
        </section>

        <section class="mb-20 lg:mb-0 bg-slate-50">
            <div class="max-w-6xl mx-auto px-4 py-12 lg:py-20">
                <!-- Textbereich -->
                <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center mb-6">Europäische KI-Aktien</h2>
                <p class="text-slate-600 leading-relaxed text-sm lg:text-base text-center lg:text-left">
                    Künstliche Intelligenz spielt auch in Europa eine zunehmend wichtige Rolle, und mehrere große Unternehmen aus unserem Euro-Titans-Paket treiben diese
                    Entwicklung aktiv voran. Zu den führenden europäischen KI-Aktien zählen ASML aus den Niederlanden, das mit seiner Chiptechnologie die Grundlage für
                    KI-Prozessoren liefert, sowie SAP aus Deutschland, das KI-gestützte Anwendungen für Unternehmenssoftware integriert.
                    <br /><br />
                    Auch Siemens nutzt KI, um industrielle Prozesse zu automatisieren und zu optimieren, während Airbus Künstliche Intelligenz in der Luft- und Raumfahrt einsetzt,
                    etwa zur Wartung, Flugsicherheit und Effizienzsteigerung. Bayer und Novo Nordisk wiederum verwenden KI in der medizinischen Forschung und Wirkstoffentwicklung.
                    <br /><br />
                    Diese Unternehmen zeigen, dass Europa nicht nur auf traditionelle Industrien setzt, sondern zunehmend zu einem starken Innovationsstandort im Bereich der
                    Künstlichen Intelligenz wird.
                </p>
            </div>
        </section>

        <!--Floating Jetzt Paket buchen button -->
        <div :class="!pricingCardInView ? 'opacity-100' : 'opacity-0'" class="fixed bottom-4 left-0 right-0 mx-auto w-fit z-50 transition duration-150 ease-in-out">
            <a href="#pricing-card" class="bg-blue text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3 hover:bg-blue-700 transition-colors ease-in-out">
                <span>Jetzt Paket buchen</span>
                <i class="fi fi-rs-shopping-cart text-white flex" />
            </a>
        </div>
    </div>
</template>

<script>
import FeatureItem from './FeatureItem.vue';
import PriceRow from './PriceRow.vue';
import FeatureCard from './FeatureCard.vue';
import ShopifyProduct from '../miscellaneous/ShopifyProducts.vue';
import Accordeon from '../miscellaneous/Accordeon.vue';
import Popup from '../miscellaneous/Popup.vue';
import Eurotitans from '../../data/eurotitans_aktien_testdaten.json';

export default {
    name: 'EuroTitans',
    components: {
        FeatureItem,
        PriceRow,
        FeatureCard,
        ShopifyProduct,
        Accordeon,
        Popup
    },
    props: {
        productGroup: {
            type: Object,
            required: true
        },
        selectedProductVariant: {
            type: Object,
            default: null
        },
        sortedProductVariants: {
            type: Array,
            default: () => []
        }
    },
    data: () => ({
        pricingCardInView: false,
        marketingModalOpen: false,
        eurotitans: Eurotitans,
        faqs: [
            {
                question: 'Was bekomme ich mit einem Europa Titans Abonnement?',
                answer: 'Mit dem Europa Titans-Paket erhalten Sie täglich, von Montag bis Freitag, eine Analyse von vier europäischen Champions. Das bedeutet, Sie bekommen alle zwei Wochen Analysen zu allen 40 Aktien des Europa Titans-Pakets. Die Analysen beinhalten konkrete Zielzonen und verständlich erklärte Chartbilder auf Basis der Elliott-Wellen-Analyse. Zusätzlich informiert Sie die HKCM per Push-Benachrichtigung und E-Mail über außergewöhnliche Marktbewegungen, Einstiege, Stoppsetzungen und Gewinnmitnahmen. Alle Inhalte stehen Ihnen dauerhaft im HKCM-Dashboard und in der App zur Verfügung. Das integrierte Musterportfolio zeigt alle Einstiege, Ausstiege, Nachkäufe und Stopps.'
            },
            {
                question: 'Wie viel kostet das Europa Titans-Aktienpaket?',
                answer: 'Die Europa Titans sind in vier Laufzeiten mit gestaffelten Monatspreisen erhältlich: <br/><br/>● Laufzeit 1 Monat: 99,95 € pro Monat <br/><br/>● Laufzeit 3 Monate: 94,95 € pro Monat (5 % Rabatt) <br/><br/>● Laufzeit 6 Monate: 89,96 € pro Monat (10 % Rabatt) <br/><br/>● Laufzeit 1 Jahr: 84,96 € pro Monat (15 % Rabatt). <br/><br/>Alle Abonnements verlängern sich nach Ablauf automatisch um die jeweils gebuchte Laufzeit.'
            },
            {
                question: 'Wie kann ich bei der HKCM bezahlen?',
                answer: 'Die HKCM bietet folgende Zahlungsmethoden an:<br/><br/>● Kreditkarte (z. B. Mastercard, Visa)<br/><br/>● PayPal<br/><br/>● Apple Pay<br/><br/>● Google Pay<br/><br/>Alle Optionen stehen Ihnen direkt im Bestellprozess auf der Webseite zur Auswahl.'
            },
            {
                question: 'Kann ich das Europa Titans-Paket in der HKCM-App buchen?',
                answer: 'Sie können unsere Pakete ausschließlich über die HKCM-Webseite buchen. In der App können Sie bestehende Abonnements einsehen und verwalten, aber kein neues Europa Titans-Abo abschließen.'
            },
            {
                question: 'Verlängert sich das Europa Titans-Abonnement automatisch?',
                answer: 'Ja, alle Europa Titans-Abonnements verlängern sich nach Ablauf automatisch um die jeweils gebuchte Laufzeit.'
            },
            {
                question: 'Was ist die HKCM?',
                answer: 'Die HKCM bietet professionelle Finanzanalysen auf Basis der Elliott-Wellen-Theorie an. Unsere Analysepakete liefern klare Marktanalysen zu Aktien, Indizes, Kryptowährungen und Rohstoffen. HKCM steht für Hopf-Klinkmüller Capital Management.'
            }
        ],
        _pricingCardObserver: null
    }),
    computed: {
        gruppierteAktien() {
            const groups = {};
            this.eurotitans.info.forEach((aktie) => {
                if (!groups[aktie.category]) {
                    groups[aktie.category] = [];
                }

                groups[aktie.category].push(aktie);
            });

            return Object.entries(groups).map(([category, aktien]) => ({
                category,
                aktien
            }));
        }
    },
    mounted() {
        this.setupPricingObserver();
    },
    beforeUnmount() {
        if (this._pricingCardObserver) {
            this._pricingCardObserver.disconnect();
            this._pricingCardObserver = null;
        }
    },
    methods: {
        getAttributeByType(productVariant, type) {
            return productVariant.productAttributeValues.data.find((productAttributeValue) => {
                return productAttributeValue.meta.type === type;
            });
        },
        closeAction() {
            this.marketingModalOpen = false;
        },
        setupPricingObserver() {
            const el = document.getElementById('pricing-card');

            if (el) {
                this._pricingCardObserver = new IntersectionObserver(
                    (entries) => {
                        const entry = entries[0];
                        this.pricingCardInView = entry.isIntersecting;
                    },
                    { root: null, threshold: 0.1 }
                );
                this._pricingCardObserver.observe(el);
            } else {
                this.$nextTick(() => {
                    const el2 = document.getElementById('pricing-card');

                    if (el2) {
                        this._pricingCardObserver = new IntersectionObserver(
                            (entries) => {
                                this.pricingCardInView = entries[0].isIntersecting;
                            },
                            { root: null, threshold: 0.1 }
                        );
                        this._pricingCardObserver.observe(el2);
                    }
                });
            }
        }
    }
};
</script>
